import React, { useState, useRef, useEffect } from 'react';
import { theme } from '../styles/theme';
import { AttachFile, Send, Mic, Cancel, CheckCircle } from '@mui/icons-material';
import './ChatInputField.css';

/**
 * ChatInputField - Message input component
 * Matches Flutter ChatInputField design
 * Includes WhatsApp-like voice recording functionality
 */
const ChatInputField = ({ onSendMessage, onTyping, onSendFile }) => {
  const [message, setMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [isCanceling, setIsCanceling] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioLevels, setAudioLevels] = useState([]);
  const typingTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  const recordingStartTimeRef = useRef(null);
  const touchStartYRef = useRef(null);
  const isSendingRef = useRef(false);
  const isCancelingRef = useRef(false);

  useEffect(() => {
    return () => {
      // Stop typing when component unmounts (as per guide)
      if (isTyping) {
        onTyping?.(false);
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      // Cleanup recording if component unmounts during recording
      if (isRecording) {
        stopRecording(false);
      }
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    };
  }, [isTyping, onTyping]);

  /**
   * Format duration in seconds to MM:SS format
   */
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  /**
   * Generate waveform bars for audio visualization
   */
  useEffect(() => {
    if (isRecording && !isPaused) {
      const waveformInterval = setInterval(() => {
        // Generate random heights for waveform bars (simulating audio levels)
        const bars = Array.from({ length: 8 }, () => Math.random() * 30 + 10);
        setAudioLevels(bars);
      }, 150);
      return () => clearInterval(waveformInterval);
    }
  }, [isRecording, isPaused]);

  /**
   * Handle pause/resume recording
   */
  const handlePauseResume = () => {
    if (!mediaRecorderRef.current) return;
    
    try {
      if (isPaused) {
        // Resume recording
        if (mediaRecorderRef.current.state === 'paused') {
          mediaRecorderRef.current.resume();
          setIsPaused(false);
        }
      } else {
        // Pause recording
        if (mediaRecorderRef.current.state === 'recording') {
          if (typeof mediaRecorderRef.current.pause === 'function') {
            mediaRecorderRef.current.pause();
            setIsPaused(true);
          } else {
            console.warn('Pause not supported in this browser');
          }
        }
      }
    } catch (error) {
      console.error('Error pausing/resuming recording:', error);
    }
  };

  /**
   * Start voice recording
   */
  const startRecording = async () => {
    try {
      // Check browser support
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        alert('Your browser does not support audio recording. Please use Chrome, Firefox, or Edge.');
        return;
      }

      if (!window.MediaRecorder) {
        alert('MediaRecorder API is not supported. Please use a modern browser.');
        return;
      }

      // CRITICAL: Get list of available audio input devices first
      // This ensures we're using the actual microphone, not system audio
      let audioInputDevices = [];
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        audioInputDevices = devices.filter(device => device.kind === 'audioinput');
      } catch (err) {
        console.warn('⚠️ Could not enumerate devices:', err);
      }
      
      // Request microphone permission - CRITICAL: Use actual microphone input
      // Start with simple constraints, then fallback if needed
      let stream = null;
      let audioConstraints = null;
      
      // Try 1: Simple audio with explicit input source (most compatible)
      try {
        // Use 'audioinput' constraint to ensure we get microphone, not system audio
        audioConstraints = { 
          audio: {
            echoCancellation: false,      // Disable to capture raw sound
            noiseSuppression: false,      // Disable to capture all sound
            autoGainControl: false       // Disable to capture natural levels
          }
        };
        stream = await navigator.mediaDevices.getUserMedia(audioConstraints);
      } catch (error1) {
        console.warn('⚠️ Simple constraints failed, trying with basic audio settings:', error1.message);
        
        // Try 2: Basic audio with optional settings
        try {
          audioConstraints = {
            audio: {
              echoCancellation: false,
              noiseSuppression: false,
              autoGainControl: false
            }
          };
          stream = await navigator.mediaDevices.getUserMedia(audioConstraints);
        } catch (error2) {
          console.warn('⚠️ Basic settings failed, trying minimal constraints:', error2.message);
          
          // Try 3: Minimal constraints (just audio, let browser decide)
          try {
            audioConstraints = { audio: {} };
            stream = await navigator.mediaDevices.getUserMedia(audioConstraints);
          } catch (error3) {
            // All attempts failed
            console.error('❌ All microphone access attempts failed');
            throw error3; // Throw the last error
          }
        }
      }
      
      // CRITICAL: Verify stream is valid and has audio tracks from microphone
      if (!stream) {
        throw new Error('Microphone stream is null - access failed');
      }
      
      const audioTracks = stream.getAudioTracks();
      if (!audioTracks || audioTracks.length === 0) {
        throw new Error('No audio tracks found in microphone stream');
      }
      
      // Log audio track settings to verify we're using the correct microphone
      const track = audioTracks[0];
      const settings = track.getSettings();
      const capabilities = track.getCapabilities ? track.getCapabilities() : {};
      
      // CRITICAL: Verify this is an audio INPUT track (microphone), not output
      if (track.kind !== 'audio') {
        console.error('❌ CRITICAL: Track is not audio type!', track.kind);
        throw new Error('Invalid audio track type - not a microphone input');
      }
      
      // Verify track is actually working and capturing
      if (track.readyState !== 'live') {
        console.warn('⚠️ Audio track is not live:', track.readyState);
        // Try to enable the track
        track.enabled = true;
        if (track.readyState !== 'live') {
          throw new Error(`Audio track not live: ${track.readyState}. Microphone may be in use.`);
        }
      }
      
      // Verify track is not muted
      if (track.muted) {
        console.warn('⚠️ Audio track is muted - recording may be silent!');
      }
      
      // Determine best MIME type
      let mimeType = '';
      const supportedTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/ogg;codecs=opus',
        'audio/ogg',
        'audio/wav'
      ];
      
      for (const type of supportedTypes) {
        if (MediaRecorder.isTypeSupported(type)) {
          mimeType = type;
          break;
        }
      }
      
      // Initialize MediaRecorder with high-quality audio settings
      // CRITICAL: Use high bitrate to capture full quality surrounding sound
      const options = mimeType ? { 
        mimeType: mimeType,
        audioBitsPerSecond: 128000  // 128 kbps for high quality audio capture
      } : {
        audioBitsPerSecond: 128000  // Set bitrate even if no mimeType specified
      };
      
      const mediaRecorder = new MediaRecorder(stream, options);
      
      // Store stream reference for cleanup
      const streamRef = stream;

      // Reset audio chunks
      audioChunksRef.current = [];
      mediaRecorderRef.current = mediaRecorder;
      recordingStartTimeRef.current = Date.now();

      // Handle data available - CRITICAL for collecting audio from microphone
      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          // Add chunk to array - this is the actual recorded audio
          audioChunksRef.current.push(event.data);
        } else {
          console.warn('⚠️ Empty audio data chunk - microphone may not be capturing sound');
        }
      };

      // Handle recording stop
      mediaRecorder.onstop = async () => {
        // Immediately show sending overlay if not canceling
        if (!isCancelingRef.current) {
          setIsSending(true);
          isSendingRef.current = true;
        }
        
        // Request final data chunk before stopping tracks
        if (mediaRecorder.state !== 'inactive') {
          mediaRecorder.requestData();
        }
        
        // Wait a moment for final data
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Stop all tracks
        streamRef.getTracks().forEach(track => {
          track.stop();
        });

        // Calculate total size
        const totalSize = audioChunksRef.current.reduce((sum, chunk) => sum + chunk.size, 0);

        // Only send if not canceled and has data
        if (!isCancelingRef.current && audioChunksRef.current.length > 0 && totalSize > 0) {
          // Ensure sending state is set (in case it wasn't set earlier)
          if (!isSendingRef.current) {
            setIsSending(true);
            isSendingRef.current = true;
          }
          
          try {
            // Calculate duration FIRST (before using it)
            // Ensure recordingStartTimeRef is valid
            if (!recordingStartTimeRef.current) {
              console.warn('⚠️ recordingStartTimeRef is null, using current time');
              recordingStartTimeRef.current = Date.now() - 1000; // Default to 1 second ago
            }
            
            // Calculate duration - ensure it's at least 1 second
            let duration = Math.floor((Date.now() - recordingStartTimeRef.current) / 1000);
            if (duration < 1) {
              console.warn('⚠️ Duration is less than 1 second, setting to 1');
              duration = 1;
            }
            
            // Get MIME type
            const actualMimeType = mediaRecorder.mimeType || 'audio/webm';
            
            // CRITICAL: Verify we have audio chunks before creating blob
            if (audioChunksRef.current.length === 0) {
              throw new Error('No audio chunks collected - recording may have failed');
            }
            
            // Create blob from raw audio chunks (actual recorded sound from YOUR microphone)
            const audioBlob = new Blob(audioChunksRef.current, { type: actualMimeType });
            
            if (audioBlob.size === 0) {
              throw new Error('Audio blob is empty - no sound was recorded from microphone');
            }
            
            // Verify blob contains actual audio data
            const blobSizeKB = (audioBlob.size / 1024).toFixed(2);
            const expectedMinSize = duration * 2; // Minimum ~2KB per second for audio

            if (audioBlob.size < expectedMinSize) {
              console.warn('⚠️ Audio blob size seems small for duration:', {
                blobSize: audioBlob.size,
                blobSizeKB: blobSizeKB,
                duration: duration,
                expectedMinSize: expectedMinSize,
                note: 'Audio might be very quiet, microphone muted, or not capturing properly'
              });
            }
            
            // Determine extension
            let fileExtension = 'webm';
            let finalMimeType = 'audio/webm';
            
            if (actualMimeType.includes('webm')) {
              fileExtension = 'webm';
              finalMimeType = 'audio/webm';
            } else if (actualMimeType.includes('mp4') || actualMimeType.includes('m4a')) {
              fileExtension = 'm4a';
              finalMimeType = 'audio/mp4';
            } else if (actualMimeType.includes('ogg')) {
              fileExtension = 'ogg';
              finalMimeType = 'audio/ogg';
            } else if (actualMimeType.includes('wav')) {
              fileExtension = 'wav';
              finalMimeType = 'audio/wav';
            }
            
            // Create file with unique timestamp to ensure it's the CURRENT recording
            // CRITICAL: Use current timestamp to prevent sending old/cached files
            const timestamp = Date.now();
            const fileName = `voice_${timestamp}.${fileExtension}`;
            
            // CRITICAL: Create fresh File object from the blob we just recorded
            // This ensures we're sending the actual recorded audio, not a cached file
            const audioFile = new File([audioBlob], fileName, { 
              type: finalMimeType,
              lastModified: timestamp
            });

            // Verify file was created correctly and matches blob
            if (audioFile.size !== audioBlob.size) {
              console.error('❌ CRITICAL: File size mismatch!', {
                blobSize: audioBlob.size,
                fileSize: audioFile.size,
                note: 'File may be corrupted or wrong file'
              });
              throw new Error('File creation error - size mismatch. Recording may be invalid.');
            }

            // CRITICAL: Verify file is from current recording session
            // Check timestamp matches current session
            const timeSinceRecordingStart = Date.now() - recordingStartTimeRef.current;
            const timeDiff = Math.abs(timeSinceRecordingStart - (duration * 1000));
            
            if (timeDiff > 2000) { // More than 2 seconds difference
              console.error('❌ CRITICAL: File timestamp mismatch!', {
                recordingStartTime: recordingStartTimeRef.current,
                fileTimestamp: timestamp,
                duration: duration,
                timeSinceStart: timeSinceRecordingStart,
                timeDiff: timeDiff,
                note: 'This file may be from a different recording session!'
              });
              throw new Error('File timestamp mismatch - wrong recording file detected');
            }
            
            // Send file - CRITICAL: Ensure onSendFile callback exists
            if (onSendFile && typeof onSendFile === 'function') {
              try {
                // CRITICAL: Create a fresh File object to ensure no caching issues
                // Clone the blob to create a completely new file object
                const blobClone = audioBlob.slice(0, audioBlob.size, audioBlob.type);
                const freshAudioFile = new File([blobClone], fileName, { 
                  type: finalMimeType,
                  lastModified: timestamp
                });

                // Call the callback with the fresh file - this should trigger handleSendFile in ChatRoomScreen
                onSendFile(freshAudioFile);
              } catch (error) {
                console.error('❌ Error calling onSendFile:', error);
                alert('Failed to send voice message. Please try again.');
                setIsSending(false);
                isSendingRef.current = false;
                return;
              }
            } else {
              console.error('❌ onSendFile callback is not available!', {
                onSendFile: onSendFile,
                type: typeof onSendFile
              });
              alert('Error: Cannot send voice message. Please refresh the page.');
              setIsSending(false);
              isSendingRef.current = false;
              return;
            }
            
            // Keep sending state visible longer to show user feedback
            // The actual upload happens in the background via ChatRoomScreen
            // Keep overlay visible for at least 5 seconds to ensure user sees it clearly
            // Don't hide immediately - let user see the sending progress
            setTimeout(() => {
              setIsSending(false);
              isSendingRef.current = false;
            }, 5000);
          } catch (error) {
            console.error('❌ Error:', error);
            setIsSending(false);
            isSendingRef.current = false;
            alert(`Failed to create recording: ${error.message}`);
          }
        } else {
          setIsSending(false);
          isSendingRef.current = false;
        }

        // Reset
        audioChunksRef.current = [];
        isCancelingRef.current = false;
        setIsCanceling(false);
      };

      mediaRecorder.onerror = (event) => {
        console.error('❌ MediaRecorder error:', event.error);
        stopRecording(false);
        alert('Recording error. Please try again.');
      };

      // Start recording with smaller timeslice for better audio capture
      // Smaller timeslice (500ms) ensures more frequent data collection for better quality
      mediaRecorder.start(500); // Collect data every 500ms for better raw audio capture
      
      setIsRecording(true);
      setRecordingDuration(0);
      isCancelingRef.current = false;
      setIsCanceling(false);

      // Start timer
      recordingIntervalRef.current = setInterval(() => {
        if (recordingStartTimeRef.current) {
          const elapsed = Math.floor((Date.now() - recordingStartTimeRef.current) / 1000);
          setRecordingDuration(elapsed);
        }
      }, 100);

    } catch (error) {
      console.error('❌ Error accessing microphone:', error);
      setIsRecording(false);
      
      // Provide specific error messages and solutions
      let errorMessage = '';
      let solution = '';
      
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        errorMessage = 'Microphone permission denied';
        solution = 'Please click the lock icon (🔒) in your browser address bar and allow microphone access, then refresh the page.';
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        errorMessage = 'No microphone found';
        solution = 'Please connect a microphone to your device and try again.';
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        errorMessage = 'Microphone is being used by another application';
        solution = 'Please close other applications using the microphone (like Zoom, Teams, Skype, etc.) and try again.';
      } else if (error.name === 'OverconstrainedError') {
        errorMessage = 'Microphone constraints not supported';
        solution = 'Your microphone does not support the requested settings. The app will try with simpler settings.';
      } else if (error.message && (error.message.includes('Could not start audio source') || 
                                   error.message.includes('could not start') ||
                                   error.message.includes('audio source'))) {
        errorMessage = 'Could not start audio source';
        solution = 'Your microphone may be in use by another application or browser tab.\n\n' +
                   'Solutions:\n' +
                   '1. Close other apps using microphone (Zoom, Teams, Skype, etc.)\n' +
                   '2. Close other browser tabs that might be using microphone\n' +
                   '3. Restart your browser\n' +
                   '4. Check microphone permissions in System Settings';
      } else {
        errorMessage = error.message || 'Unknown error';
        solution = 'Please try:\n' +
                   '1. Check microphone permissions in browser settings\n' +
                   '2. Ensure no other app is using the microphone\n' +
                   '3. Refresh the page and try again\n' +
                   '4. Restart your browser if problem persists';
      }
      
      // Show user-friendly error with solution
      const fullMessage = `${errorMessage}\n\n${solution}\n\nError type: ${error.name || 'Unknown'}`;
      alert(fullMessage);
      
      // Log detailed error for debugging
      console.error('📋 Detailed microphone error:', {
        name: error.name,
        message: error.message,
        constraint: error.constraint,
        stack: error.stack
      });
    }
  };

  /**
   * Stop voice recording
   */
  const stopRecording = (shouldSend = true) => {
    if (!isRecording || !mediaRecorderRef.current) {
      console.warn('⚠️ Cannot stop recording - not recording or MediaRecorder not available');
      return;
    }

    // Clear timer
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }

    // Mark as canceling if not sending
    if (!shouldSend) {
      isCancelingRef.current = true;
      setIsCanceling(true);
      setIsSending(false);
      isSendingRef.current = false;
    } else {
      // If sending, show sending overlay immediately
      isCancelingRef.current = false;
      setIsCanceling(false);
      setIsSending(true);
      isSendingRef.current = true;
    }

    // Request final data chunk before stopping
    if (mediaRecorderRef.current.state === 'recording') {
      try {
        mediaRecorderRef.current.requestData();
      } catch (e) {
        console.warn('⚠️ Could not request final data:', e);
      }
    }

    // Stop recording
    if (mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop();
      } catch (e) {
        console.error('❌ Error stopping MediaRecorder:', e);
        // Still try to show sending if we were supposed to send
        if (shouldSend) {
          setIsSending(true);
          isSendingRef.current = true;
        }
      }
    }

    setIsRecording(false);
    setRecordingDuration(0);
    setIsPaused(false);
    setAudioLevels([]);
    recordingStartTimeRef.current = null;
  };

  /**
   * Handle microphone button mouse/touch down - start recording
   */
  const handleMicDown = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (message.trim().length > 0) {
      return; // Don't record if there's text
    }
    
    if (isRecording) {
      return;
    }
    
    if (isSending) {
      return;
    }
    
    touchStartYRef.current = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    startRecording();
  };

  /**
   * Handle microphone button mouse/touch up - stop recording and send
   */
  const handleMicUp = (e) => {
    e.preventDefault();
    if (!isRecording) return;

    const currentY = e.type === 'touchend' ? (e.changedTouches?.[0]?.clientY || touchStartYRef.current) : e.clientY;
    const startY = touchStartYRef.current || currentY;
    const deltaY = startY - currentY;

    // If user swiped up more than 50px, cancel recording
    if (deltaY > 50) {
      stopRecording(false);
    } else {
      stopRecording(true);
    }

    touchStartYRef.current = null;
  };

  /**
   * Handle mouse/touch move - detect swipe up to cancel
   */
  const handleMicMove = (e) => {
    if (!isRecording) return;

    const currentY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
    const startY = touchStartYRef.current || currentY;
    const deltaY = startY - currentY;

    // Show cancel indicator if swiped up
    if (deltaY > 50) {
      setIsCanceling(true);
    } else {
      setIsCanceling(false);
    }
  };

  /**
   * Handle cancel button click
   */
  const handleCancelRecording = () => {
    stopRecording(false);
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setMessage(value);

    // Handle typing indicator - matches Flutter implementation
    // Start typing after 1-2 characters (as per guide)
    const hasText = value.trim().length > 0;
    
    if (hasText && !isTyping) {
      // Start typing
      setIsTyping(true);
      onTyping?.(true);

      // Auto-stop after 3 seconds of inactivity
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(false);
        onTyping?.(false);
      }, 3000);
    } else if (!hasText && isTyping) {
      // Stop typing if text is cleared
      setIsTyping(false);
      onTyping?.(false);
      
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    } else if (hasText) {
      // Reset timer on each keystroke (as per guide)
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(false);
        onTyping?.(false);
    }, 3000);
    }
  };

  const handleSend = () => {
    if (message.trim().length > 0) {
      onSendMessage(message.trim());
      setMessage('');
      setIsTyping(false);
      onTyping?.(false);
      
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type + size before sending to ChatRoomScreen
      const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
      const fileName = String(file.name || '');
      const ext = fileName.includes('.') ? fileName.split('.').pop().toLowerCase() : '';

      const allowedImageExt = new Set(['jpg', 'jpeg', 'png']);
      const allowedDocExt = new Set(['pdf', 'doc', 'xls', 'xlsx']);
      const allowedMediaExt = new Set(['mp3', 'wav', 'mov']);

      const isAllowed =
        (ext && allowedImageExt.has(ext)) ||
        (ext && allowedDocExt.has(ext)) ||
        (ext && allowedMediaExt.has(ext));

      if (!isAllowed) {
        alert(
          'Invalid file type. Allowed:\n' +
            'Images: .jpg, .jpeg, .png\n' +
            'Documents: .pdf, .doc, .xls, .xlsx\n' +
            'Media: .mp3, .wav, .mov'
        );
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        alert('File size exceeds 5MB. Please select a smaller file.');
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      if (onSendFile && typeof onSendFile === 'function') {
        onSendFile(file);
      } else {
        console.error('❌ onSendFile is not available or not a function:', {
          onSendFile: onSendFile,
          type: typeof onSendFile
        });
        alert('Error: File sending is not available. Please refresh the page.');
      }
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } else {
      console.warn('⚠️ No file selected');
    }
  };

  const handleAttachClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isRecording || isSending) {
      console.warn('⚠️ Cannot attach file while recording or sending');
      return;
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.click();
    } else {
      console.error('❌ fileInputRef.current is null! File input not found.');
      alert('Error: File input not available. Please refresh the page.');
    }
  };

  return (
    <div 
      className="chat-input-container"
      style={{
        display: 'flex',
        flexDirection: 'column',
        visibility: 'visible',
        opacity: 1,
        position: 'relative',
        zIndex: 1000,
        width: '100%',
        backgroundColor: 'white',
        padding: window.innerWidth <= 640 ? '0.875rem' : '1.25rem',
        borderTop: '1px solid #f5f5f4',
        boxSizing: 'border-box',
        flexShrink: 0,
        flexGrow: 0,
        boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.05)'
      }}
    >

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%' }}>
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
          accept=".jpg,.jpeg,.png,.pdf,.doc,.xls,.xlsx,.mp3,.wav,.mov"
          multiple={false}
        />
        
        {/* Attach button */}
        {/* <button
          onClick={handleAttachClick}
          disabled={isRecording || isSending}
          style={{
            padding: '0.625rem',
            color: '#a8a29e',
            backgroundColor: '#fafaf9',
            borderRadius: '9999px',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            height: '2.75rem',
            width: '2.75rem',
            border: '1px solid #e7e5e4',
            cursor: (isRecording || isSending) ? 'not-allowed' : 'pointer',
            opacity: (isRecording || isSending) ? 0.5 : 1
          }}
          onMouseEnter={(e) => {
            if (!isRecording && !isSending) {
              e.currentTarget.style.color = '#f59e0b';
            }
          }}
          onMouseLeave={(e) => {
            if (!isRecording && !isSending) {
              e.currentTarget.style.color = '#a8a29e';
            }
          }}
          title="Attach file"
        >
          <span className="material-icons-outlined" style={{ fontSize: '1.25rem', transform: 'rotate(45deg)' }}>attach_file</span>
        </button> */}

        {/* Input container */}
        {isRecording ? (
          <div style={{
            flex: 1,
            backgroundColor: '#f5f5dc',
            borderRadius: '9999px',
            padding: '0.625rem 1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            border: '1px solid #e0dcc8',
            minHeight: '2.75rem'
          }}>
            {/* Red recording indicator */}
            <div style={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              backgroundColor: '#ff0000',
              flexShrink: 0,
              animation: isPaused ? 'none' : 'pulse 1.5s ease-in-out infinite'
            }} />
            
            {/* Timer */}
            <span style={{ 
              fontSize: '0.875rem', 
              color: '#ff6b35',
              fontWeight: '500',
              fontVariantNumeric: 'tabular-nums',
              minWidth: '40px'
            }}>
              {formatDuration(recordingDuration)}
            </span>
            
            {/* Audio Waveform */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '3px',
              flex: 1,
              height: '24px',
              justifyContent: 'center'
            }}>
              {audioLevels.length > 0 ? (
                audioLevels.map((height, index) => (
                  <div
                    key={index}
                    style={{
                      width: '3px',
                      height: `${height}px`,
                      backgroundColor: '#ff6b35',
                      borderRadius: '2px',
                      transition: 'height 0.15s ease'
                    }}
                  />
                ))
              ) : (
                Array.from({ length: 8 }).map((_, index) => (
                  <div
                    key={index}
                    style={{
                      width: '3px',
                      height: '12px',
                      backgroundColor: '#ff6b35',
                      borderRadius: '2px',
                      opacity: 0.5
                    }}
                  />
                ))
              )}
            </div>
            
            {/* Delete button */}
            <button
              onClick={handleCancelRecording}
              style={{
                background: 'none',
                border: 'none',
                padding: '0.375rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#9ca3af',
                flexShrink: 0
              }}
              title="Delete"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
            
            {/* Pause button */}
            <button
              onClick={handlePauseResume}
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                border: '2px solid #ff6b35',
                background: isPaused ? '#ff6b35' : '#f5f5dc',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                padding: 0
              }}
              title={isPaused ? "Resume" : "Pause"}
            >
              {isPaused ? (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#f5f5dc" strokeWidth="3">
                  <polygon points="5 3 19 12 5 21" />
                </svg>
              ) : (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ff6b35" strokeWidth="3">
                  <line x1="9" y1="9" x2="9" y2="15" />
                  <line x1="15" y1="9" x2="15" y2="15" />
                </svg>
              )}
            </button>
          </div>
        ) : isSending ? (
          <div style={{
            flex: 1,
            backgroundColor: '#fafaf9',
            borderRadius: '9999px',
            padding: '0.625rem 1.25rem',
            display: 'flex',
            alignItems: 'center',
            border: '1px solid #e7e5e4'
          }}>
            <span style={{ fontSize: '0.875rem', color: '#78716c' }}>Sending voice message...</span>
          </div>
        ) : (
          <div 
            style={{
              flex: 1,
              backgroundColor: '#fafaf9',
              borderRadius: '9999px',
              padding: '0.625rem 1.25rem',
              display: 'flex',
              alignItems: 'center',
              border: '1px solid #e7e5e4',
              transition: 'all 0.2s',
              boxShadow: 'inset 0 1px 2px 0 rgba(0, 0, 0, 0.05)'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#f59e0b';
              e.currentTarget.style.boxShadow = '0 0 0 2px rgba(245, 158, 11, 0.1)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = '#e7e5e4';
              e.currentTarget.style.boxShadow = 'inset 0 1px 2px 0 rgba(0, 0, 0, 0.05)';
            }}
          >
            <input
              type="text"
              placeholder="Type a message..."
              value={message}
              onChange={handleChange}
              onKeyPress={handleKeyPress}
              style={{
                flex: 1,
                backgroundColor: 'transparent',
                border: 'none',
                outline: 'none',
                fontSize: '0.875rem',
                color: '#1c1917',
                padding: 0
              }}
            />
          </div>
        )}

        {/* Send button - always shown (disabled when empty) */}
        {!isRecording && !isSending && (
          <button
            onClick={handleSend}
            disabled={message.trim().length === 0}
            style={{
              backgroundColor: '#0d9488',
              color: 'white',
              padding: '0.75rem',
              borderRadius: '9999px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              height: '2.75rem',
              width: '2.75rem',
              cursor: 'pointer',
              border: 'none'
            }}
            onMouseEnter={(e) => {
              if (message.trim().length === 0) return;
              e.currentTarget.style.backgroundColor = '#115e59';
              e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(13, 148, 136, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#0d9488';
              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
            }}
            onMouseDown={(e) => {
              if (message.trim().length === 0) return;
              e.currentTarget.style.transform = 'scale(0.95)';
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
            title="Send message"
          >
            <span className="material-icons-outlined" style={{ fontSize: '1.25rem', marginLeft: '0.125rem' }}>send</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default ChatInputField;

